import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { Edit as EditIcon, OpenInNew, Add as AddIcon } from "@material-ui/icons";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TaskEditModal from "../../components/TaskEditModal";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll", overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const PERIOD_RANGE = {
  week: () => ({ from: moment().startOf("week").toISOString(), to: moment().endOf("week").toISOString() }),
  month: () => ({ from: moment().startOf("month").toISOString(), to: moment().endOf("month").toISOString() }),
};

// Pagina dedicada de tarefas do checklist do Kanban - pedido do usuario
// 2026-08-29 ("acho que vc deveria criar uma nova pagina para
// trabalharmos com as tarefas") depois que o popover "Minhas Tarefas"
// (ver components/MyTasksPopover) se mostrou pequeno demais pra editar/
// filtrar direito. Reusa o mesmo endpoint /my-tasks e a mesma regra de
// atrasada-sempre-aparece do backend.
const Tasks = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [includeDone, setIncludeDone] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Pedido do usuario 2026-10-01: "na tela de tarefas preciso que
  // consiga criar por la tambem" - antes so' dava pra criar tarefa de
  // dentro de um card (BoardCardModal). Reaproveita o mesmo
  // TaskEditModal, que ganhou um modo de criacao (ver o componente).
  const [creatingTask, setCreatingTask] = useState(false);
  // Pedido do usuario 2026-10-02: "se eu quiser apenas verificar as
  // tarefas atribuidas para o jean, deveria aparecer" - antes a tela so'
  // mostrava as tarefas do usuario logado, sem jeito de ver as de outra
  // pessoa da empresa. "" = minhas tarefas (default, comportamento
  // original).
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");

  useEffect(() => {
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  // O select de periodo e' so' um atalho pra preencher De/Ate - quem
  // manda pra query e' sempre customFrom/customTo, editaveis a qualquer
  // momento (o backend em /my-tasks ja aplica a regra de "atrasada e nao
  // concluida sempre aparece", mesmo fora do periodo escolhido).
  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (PERIOD_RANGE[value]) {
      const range = PERIOD_RANGE[value]();
      setCustomFrom(moment(range.from).format("YYYY-MM-DD"));
      setCustomTo(moment(range.to).format("YYYY-MM-DD"));
    } else {
      setCustomFrom("");
      setCustomTo("");
    }
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (includeDone) params.includeDone = "true";
      if (customFrom) params.from = moment(customFrom).startOf("day").toISOString();
      if (customTo) params.to = moment(customTo).endOf("day").toISOString();
      // Pedido do usuario 2026-10-02: "se eu quiser apenas verificar as
      // tarefas atribuidas para o jean, deveria aparecer" - "" = minhas
      // tarefas (comportamento original, backend usa o usuario logado
      // quando userId nao e' mandado).
      if (filterUserId) params.userId = filterUserId;
      const { data } = await api.get("/my-tasks", { params });
      setTasks(data || []);
    } catch (err) {
      // silencioso, mesma politica do popover
    } finally {
      setLoading(false);
    }
  }, [customFrom, customTo, includeDone, filterUserId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!user?.companyId) return undefined;
    const companyId = user.companyId;
    const onCardEvent = (data) => {
      if (["task-added", "task-updated", "task-removed"].includes(data.action)) {
        fetchTasks();
      }
    };
    socket.on(`company-${companyId}-card`, onCardEvent);
    return () => {
      socket.off(`company-${companyId}-card`, onCardEvent);
    };
  }, [user, socket, fetchTasks]);

  const handleToggleDone = async (task) => {
    try {
      await api.put(`/card-tasks/${task.id}`, { done: !task.done });
      fetchTasks();
    } catch (err) {
      // toastError nao importado de proposito - erro silencioso aqui so
      // reverteria visualmente no proximo fetch, sem precisar de toast
    }
  };

  const openCard = (task) => {
    const card = task.card;
    if (card?.boardId && card?.id) {
      history.push(`/boards?board=${card.boardId}&card=${card.id}`);
    }
  };

  return (
    <MainContainer>
      <TaskEditModal
        open={!!editingTask || creatingTask}
        task={editingTask}
        onClose={() => {
          setEditingTask(null);
          setCreatingTask(false);
        }}
        onSaved={fetchTasks}
      />
      <MainHeader>
        <Title>Tarefas ({tasks.length})</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreatingTask(true)}
          >
            Nova tarefa
          </Button>
          <Select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            variant="outlined"
            margin="dense"
            displayEmpty
            style={{ height: 40, minWidth: 160 }}
          >
            <MenuItem value="">
              <em>Minhas tarefas</em>
            </MenuItem>
            <MenuItem value="all">
              <em>Todas</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={period}
            onChange={(e) => handlePeriodChange(e.target.value)}
            variant="outlined"
            margin="dense"
            style={{ height: 40 }}
          >
            <MenuItem value="all">Todo período</MenuItem>
            <MenuItem value="week">Esta semana</MenuItem>
            <MenuItem value="month">Este mês</MenuItem>
          </Select>
          <TextField
            label="De"
            type="date"
            size="small"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
            }
            label="Mostrar concluídas"
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: 48 }}>Feita</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Card</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Prazo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const overdue = !task.done && task.dueDate && moment(task.dueDate).isBefore(moment());
              return (
                <TableRow
                  key={task.id}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => setEditingTask(task)}
                >
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={task.done} onChange={() => handleToggleDone(task)} />
                  </TableCell>
                  <TableCell>
                    <span style={task.done ? { textDecoration: "line-through", color: "#9e9e9e" } : undefined}>
                      {task.title}
                    </span>
                    {task.note && (
                      <Typography variant="caption" color="textSecondary" component="div">
                        {task.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.card?.title || task.card?.board?.name || "-"}
                    {task.card?.phase?.name && (
                      <Typography variant="caption" color="textSecondary" component="div">
                        {task.card.board?.name} · {task.card.phase.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.assignedToAll ? "Todos" : task.assignedUser?.name || "-"}
                    {task.createdByUser && (
                      <Typography variant="caption" color="textSecondary" component="div">
                        Criada por {task.createdByUser.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.dueDate ? (
                      <span style={overdue ? { color: "#c62828", fontWeight: "bold" } : undefined}>
                        {overdue ? "Atrasada: " : ""}
                        {moment(task.dueDate).format("DD/MM/YYYY HH:mm")}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => setEditingTask(task)} title="Editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openCard(task)} title="Abrir card">
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton key="skeleton" columns={6} />}
            {!loading && tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhuma tarefa encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Tasks;
